import { Check } from './icons';
import styles from './CaseStudies.module.css';

const FINDS = [
  <>
    Scripts that will hit <code>SSS_USAGE_LIMIT_EXCEEDED</code> — and the record count where they fail
  </>,
  'Redundant user-event trigger stacks that are ripe for consolidation',
  'Dead weight — stale forms, unused saved searches, dormant records & fields',
  'A "Can we change it?" call on every item — your code vs. vendor SuiteApps',
];

const CARDS = [
  {
    badge: 'A',
    tag: 'NetSuite · SuiteFlow',
    title: 'Custom Approvals',
    foot: 'Approval Routing',
    desc: 'Using custom records, SuiteFlow, and SuiteScript, I tailor approval processes to your company structure and rules — by department or location, across classes, and at the transaction line level. Coverage continues when approvers are out, and approvals can happen in NetSuite or by email.',
  },
  {
    badge: 'R',
    tag: 'Inventory · Mobile',
    title: 'Rethinking Restocking',
    foot: 'Procurement',
    desc: 'A hybrid restocking solution: using custom item/location details and a barcode-enabled device, your team walks the shelves, scans a product, counts on-hand, and the app determines reorder quantities — generating Purchase Requisitions so Purchasing decides the right vendor and cost, not the folks in the warehouse.',
  },
  {
    badge: 'S',
    tag: 'Procurement · Logic',
    title: 'Smart Requisitions',
    foot: 'Cost Control',
    desc: 'Native NetSuite forces a Purchase Order for every requisition line. Smart Requisitions instead determines the best path — Purchase Order, Transfer Order from another location, or closing the request — so you control inventory and reduce purchase costs.',
  },
];

export default function CaseStudies() {
  return (
    <section className="section" id="cases">
      <div className="wrap">
        <div className="reveal">
          <span className="eyebrow">Selected Work</span>
          <h2 className="section-title" style={{ marginTop: 16 }}>
            Case Studies
          </h2>
          <p className="section-lead">
            Real NetSuite challenges — from AI-powered audits to custom records, SuiteFlow, and
            SuiteScript.
          </p>
        </div>

        <article className={`${styles.csFeature} reveal`}>
          <span className="ai-badge">AI-powered</span>
          <div className={styles.csFeatureMain}>
            <span className={styles.tag}>
              <b>★</b> NetSuite · AI · SuiteScript Governance
            </span>
            <h3>AI-Powered Performance Audit</h3>
            <p>
              NetSuite's built-in APM tells you what's slow <em>today</em>. It can't tell you which
              scripts are about to blow their governance budget, which customizations are dead weight,
              or which are vendor code you can't touch. The Performance Audit answers all three.
            </p>
            <p className={styles.csFeatureHow}>
              I pull your customizations and script source down with SuiteCloud SDF, then analyze them
              locally with AI and a live NetSuite MCP connection — scoring every script against its real
              governance unit budget, walking the call graph from each entry point through its libraries,
              and flagging redundant trigger stacks and dead configuration. It's read-only and
              reversible: a scoped role with no write access, nothing that touches your data, and every
              finding traced to a file and line you can verify.
            </p>
            <a href="#contact" className="btn btn-primary">
              Get a Performance Audit
            </a>
          </div>
          <div className={styles.csFeatureSide}>
            <span className={styles.csSideLabel}>What it surfaces</span>
            <ul className={styles.csFinds}>
              {FINDS.map((node, i) => (
                <li key={i}>
                  <Check />
                  <span>{node}</span>
                </li>
              ))}
            </ul>
            <div className={styles.csDeliverable}>
              <b>The deliverable:</b> a plain-English workbook — Summary, a governance-weighted Fix
              Order, Hot Record Types, and cleanup checklists — that a non-technical stakeholder can
              read, prioritize, and assign.
            </div>
          </div>
        </article>

        <div className={styles.csGrid}>
          {CARDS.map((c) => (
            <div className={`${styles.csCard} reveal`} key={c.badge}>
              <span className={styles.tag}>
                <b>{c.badge}</b> {c.tag}
              </span>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
              <div className={styles.line} />
              <span className={styles.foot}>{c.foot}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
